// Shared RTCR site plan constants.
//
// Plan type codes are stored as RTCR1/RTCR2/RTCR3 and follow the three ways
// 40 CFR 141.853(a)(5) allows repeat sampling locations to be identified.
// The portal no longer asks systems to pick an "option": the standard
// procedure is the default, and an SOP can be attached. Alternative fixed
// locations (code 2) are arranged with MSDH directly, not through the portal.

const RTCR_SUBPART_URL =
  "https://www.ecfr.gov/current/title-40/chapter-I/subchapter-D/part-141/subpart-Y"
const RTCR_RULE_URL = RTCR_SUBPART_URL + "/section-141.853"

const RTCR_PLAN_TYPE_LABELS = {
  1: "Standard (repeat samples within five service connections)",
  2: "Alternative fixed repeat sampling locations",
  3: "Standard Operating Procedure (SOP)"
}

// accepts "1", 1, or "RTCR1"
function rtcrPlanTypeLabel(code) {
  const key = String(code || "").replace(/^RTCR/, "")
  return RTCR_PLAN_TYPE_LABELS[key] || code
}

// Guidance documents. Replace with the 2026-30 versions from BPWS when they
// arrive.
const RTCR_GUIDANCE_URL = "/static/docs/2021-25-RTCRSampleSitePlanOption1-OTHER.pdf"

// Routine samples per month by population (required: 40 CFR 141.854 for
// 1,000 or fewer, 141.857(b) above) and the minimum number of sites MSDH
// recommends a plan list. [population from, to, required, recommended]
const RTCR_SITE_COUNT_TABLE = [
  [25, 1000, 1, 4],
  [1001, 2500, 2, 5],
  [2501, 3300, 3, 6],
  [3301, 4100, 4, 7],
  [4101, 4900, 5, 8],
  [4901, 5800, 6, 9],
  [5801, 6700, 7, 10],
  [6701, 7600, 8, 11],
  [7601, 8500, 9, 12],
  [8501, 12900, 10, 13],
  [12901, 17200, 15, 20],
  [17201, 21500, 20, 25],
  [21501, 25000, 25, 30],
  [25001, 33000, 30, 35],
  [33001, 41000, 40, 50],
  [41001, 50000, 50, 60],
  [50001, 59000, 60, 70],
  [59001, 70000, 70, 80],
  [70001, 83000, 80, 95],
  [83001, 96000, 90, 105],
  [96001, 130000, 100, 120],
  [130001, 220000, 120, 140],
  [220001, 320000, 150, 160]
]

// Suggested values for a sample point's Location (stored as description). Free text
// is still accepted: existing points carry ~280 distinct values. Entry points
// are left out on purpose: TCR samples are taken in distribution.
const RTCR_LOCATION_OPTIONS = [
  "OUTSIDE FAUCET",
  "INSIDE FAUCET",
  "SAMPLE STATION"
]

const SAMPLE_POINT_REQUIRED_FIELDS = ["address", "city", "zipcode", "description"]

// field -> label, in form order
const SAMPLE_POINT_FIELD_LABELS = {
  address: "Street Address",
  city: "City",
  zipcode: "Zip Code",
  description: "Location",
  note: "Note"
}

// fields a system can change on a sample point; `edited` means one of these
// differs from what MSDH has on file
const SAMPLE_POINT_TRACKED_FIELDS = [
  "address",
  "city",
  "zipcode",
  "description",
  "note",
  "status",
  "latitude",
  "longitude"
]

const ZIP_PATTERN = /^\d{5}(-\d{4})?$/

// compare text the way a reviewer would: case, padding and repeated spaces
// do not make two addresses different
function normalizeText(value) {
  return String(value == null ? "" : value)
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase()
}

function isBlank(value) {
  return normalizeText(value) === ""
}

function isValidZip(value) {
  return ZIP_PATTERN.test(String(value == null ? "" : value).trim())
}

function sameText(a, b) {
  return normalizeText(a) === normalizeText(b)
}

function trackedValue(point, field) {
  const value = point[field]
  if (field === "latitude" || field === "longitude") {
    return value == null || value === "" ? null : Number(value)
  }
  return field === "status" ? value : normalizeText(value)
}

// true when a point differs from the copy on file in a field the system
// controls; a new point has no copy on file
function samplePointChanged(point, on_file) {
  if (!on_file) {
    return false
  }
  return samplePointKey(point) !== samplePointKey(on_file)
}

// A point's tracked fields as one comparable string. Every change measure on
// the site plan page (edited flag, change summary, unsubmitted count) goes
// through this, so they cannot disagree.
function samplePointKey(point) {
  return JSON.stringify(
    SAMPLE_POINT_TRACKED_FIELDS.map((field) => trackedValue(point, field))
  )
}

// Next TC#### ID after the highest standard one; ignores IDs on file that do
// not follow the pattern (TCRRR, TC-0001B).
function nextSampleId(points) {
  const max = (points || []).reduce((m, p) => {
    const match = /^TC(\d{4})$/.exec(String(p.sp_id || "").trim())
    return match ? Math.max(m, parseInt(match[1], 10)) : m
  }, 0)
  return "TC" + String(max + 1).padStart(4, "0")
}

// Largest SOP file the server accepts (server/rtcr_sop.py MAX_SOP_BYTES)
const SOP_MAX_BYTES = 25 * 1024 * 1024

// Plan statuses a system may still amend: in review, follow-up required,
// rejected. Only the latest submission can be amended.
const AMENDABLE_STATUSES = ["N", "F", "R"]

// How long a submission stays current. Past this it is history: the landing
// page does not show its status and it cannot be amended. A plan in review
// is always shown (but not amendable past RTCR_RECENT_DAYS); a rejection is
// final, so it is current only briefly. Keep in step with the server's
// rtcr_amend_error.
const RTCR_RECENT_DAYS = 183
const RTCR_REJECTED_RECENT_DAYS = 30

// Whole days since submission; submission_ts is "YYYY-MM-DD HH:MM:SS TZ".
function planAgeDays(plan, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((plan && plan.submission_ts) || "")
  if (!m) return null
  const sent = Date.UTC(+m[1], +m[2] - 1, +m[3])
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((today - sent) / 86400000)
}

// Days a plan in this status stays amendable (and, except for plans in
// review, shown on the landing page).
function recentDays(status) {
  return status === "R" ? RTCR_REJECTED_RECENT_DAYS : RTCR_RECENT_DAYS
}

function isRecentPlan(plan, now = new Date()) {
  const age = planAgeDays(plan, now)
  return age !== null && age <= recentDays(plan.status)
}

// Whether the landing page shows this plan's review status.
function showPlanStatus(plan, now = new Date()) {
  if (!plan) return false
  return ["N", "F"].includes(plan.status) || isRecentPlan(plan, now)
}

function latestPlan(plans) {
  return (plans || []).reduce(
    (latest, e) => (!latest || e.plan_id > latest.plan_id ? e : latest),
    null
  )
}

function canAmendPlan(plans, plan_id) {
  const latest = latestPlan(plans)
  return (
    !!latest &&
    plan_id != null &&
    String(latest.plan_id) === String(plan_id) &&
    AMENDABLE_STATUSES.includes(latest.status) &&
    isRecentPlan(latest)
  )
}

// Validate the create/edit sample point form. `points` is the plan being
// edited; `editing` is the point the form edits (null when creating), which
// is skipped by the duplicate check. The duplicate check keys on street
// address + note, because separate taps at one address (mobile home lots,
// apartments) are normal and are told apart by the note. It only runs when
// the address or note is new, so a correction to one field of an existing
// duplicate still saves.
function samplePointFormErrors(form, points, editing) {
  const errors = []
  const missing = SAMPLE_POINT_REQUIRED_FIELDS.filter((f) => isBlank(form[f]))
  if (missing.length) {
    errors.push(
      "All fields except Note are required. Missing: " +
        missing.map((f) => SAMPLE_POINT_FIELD_LABELS[f]).join(", ") +
        "."
    )
  }
  if (!isBlank(form.zipcode) && !isValidZip(form.zipcode)) {
    errors.push("Zip Code must be 5 digits, for example 39201.")
  }
  const address_or_note_changed =
    !editing ||
    normalizeText(editing.address) !== normalizeText(form.address) ||
    normalizeText(editing.note) !== normalizeText(form.note)
  if (!isBlank(form.address) && address_or_note_changed) {
    const duplicate = (points || []).find(
      (p) =>
        p !== editing &&
        normalizeText(p.address) === normalizeText(form.address) &&
        normalizeText(p.note) === normalizeText(form.note)
    )
    if (duplicate) {
      errors.push(
        "This sample point could not be " +
          (editing ? "saved" : "created") +
          ": sample point " +
          duplicate.sp_id +
          " already has this street address" +
          (isBlank(form.note) ? "" : " and note") +
          ". If this is a separate tap at the same address, use the Note to" +
          " tell them apart (for example, Lot 4)."
      )
    }
  }
  return errors
}

// Split SDWIS note_2 ("city, zip" by convention) into city and zip. The field
// is free text on file, so it also arrives as "TYLERTOWN 39667",
// "MEADVILLE, MS", "MARIETTA, MS, MS 38856" or "YAZOO CITY MS., MS 39194".
// A zip is taken from anywhere, the state is dropped, and repeated city parts
// collapse. `check` is true when the text still does not fit (two different
// city parts, two different zips, or digits left in the city), in which case
// the raw text is kept as the city so nothing on file is lost.
// Keep in step with split_city_zip in office_mswater server/model.py: the
// office review splits the same text to show what a system changed.
function splitCityZip(note_2) {
  const raw = String(note_2 == null ? "" : note_2).trim()
  const zip_re = /\b\d{5}(?:-\d{4})?\b/g
  const zips = [...new Set(raw.match(zip_re) || [])]
  const cities = []
  raw.split(",").forEach((part) => {
    const city = part
      .replace(zip_re, " ")
      .replace(/(^|\s)MS\.?(?=\s|$)/gi, " ")
      .replace(/\s+/g, " ")
      .trim()
    if (city && !cities.some((c) => c.toUpperCase() === city.toUpperCase())) {
      cities.push(city)
    }
  })
  const check = zips.length > 1 || cities.length > 1 || /\d/.test(cities.join(""))
  if (check) {
    return { city: raw, zipcode: "", check: true }
  }
  return { city: cities[0] || "", zipcode: zips[0] || "", check: false }
}

// What a submission changes compared with the sites on file, by kind.
// `on_file` is indexed by sp_id.
function summarizePlanChanges(points, on_file) {
  const summary = { added: 0, changed: 0, inactivated: 0, reactivated: 0 }
  points.forEach((point) => {
    const file_point = on_file[point.sp_id]
    if (point.new_record || !file_point) {
      summary.added++
    } else if (point.status !== file_point.status) {
      summary[point.status == "A" ? "reactivated" : "inactivated"]++
    } else if (samplePointChanged(point, file_point)) {
      summary.changed++
    }
  })
  summary.total =
    summary.added + summary.changed + summary.inactivated + summary.reactivated
  return summary
}

export {
  RTCR_RULE_URL,
  RTCR_SUBPART_URL,
  RTCR_GUIDANCE_URL,
  RTCR_SITE_COUNT_TABLE,
  SOP_MAX_BYTES,
  RTCR_PLAN_TYPE_LABELS,
  RTCR_LOCATION_OPTIONS,
  rtcrPlanTypeLabel,
  isValidZip,
  sameText,
  samplePointChanged,
  samplePointKey,
  nextSampleId,
  RTCR_RECENT_DAYS,
  RTCR_REJECTED_RECENT_DAYS,
  latestPlan,
  isRecentPlan,
  showPlanStatus,
  canAmendPlan,
  samplePointFormErrors,
  splitCityZip,
  summarizePlanChanges
}
